import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Lock, ArrowLeft, ArrowRight } from "lucide-react";
import { api, errMsg, FIELD, AREA } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/lib/site";
import { PageHero } from "@/components/common/Reveal";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["About You", "Services", "Your Goals", "Review"];
const EMPTY = { full_name: "", email: "", phone: "", city: "", filing_status: "", services: [], business_owner: false, business_name: "", goals: "", preferred_contact: "email", referral: "" };

const Choice = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-xl border p-5 text-left text-sm transition-colors duration-300 ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/15 text-white/80 hover:border-white/40"}`}>
    {children}
  </button>
);

const StepAbout = ({ f, set }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Full Name" required><Input className={FIELD} value={f.full_name} onChange={set("full_name")} data-testid="intake-name-input" /></Field>
    <Field label="Email" required><Input className={FIELD} type="email" value={f.email} onChange={set("email")} data-testid="intake-email-input" /></Field>
    <Field label="Phone" required><Input className={FIELD} value={f.phone} onChange={set("phone")} data-testid="intake-phone-input" /></Field>
    <Field label="City"><Input className={FIELD} value={f.city} onChange={set("city")} data-testid="intake-city-input" /></Field>
    <Field label="Tax Filing Status" className="sm:col-span-2">
      <Select value={f.filing_status} onValueChange={set("filing_status")}>
        <SelectTrigger className={FIELD} data-testid="intake-filing-select"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
        <SelectContent>{["Single", "Married filing jointly", "Married filing separately", "Head of household", "Not sure"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  </div>
);

const StepServices = ({ f, setF }) => {
  const toggle = (s) => setF({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] });
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-4">
        {SERVICE_OPTIONS.map((s, i) => <Choice key={s} active={f.services.includes(s)} onClick={() => toggle(s)} testid={`intake-service-${i}`}>{s}</Choice>)}
      </div>
      <div>
        <p className="eyebrow mb-4">Do you own a business?</p>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <Choice active={f.business_owner} onClick={() => setF({ ...f, business_owner: true })} testid="intake-business-yes">Yes</Choice>
          <Choice active={!f.business_owner} onClick={() => setF({ ...f, business_owner: false, business_name: "" })} testid="intake-business-no">No</Choice>
        </div>
        {f.business_owner && <Field label="Business Name" className="mt-6 max-w-md"><Input className={FIELD} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="intake-business-name-input" /></Field>}
      </div>
    </div>
  );
};

const StepGoals = ({ f, set }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="What are your financial goals?" className="sm:col-span-2"><Textarea className={AREA} value={f.goals} onChange={set("goals")} placeholder="e.g. Maximize my refund, launch my LLC, improve my credit…" data-testid="intake-goals-input" /></Field>
    <Field label="Preferred Contact Method">
      <Select value={f.preferred_contact} onValueChange={set("preferred_contact")}>
        <SelectTrigger className={FIELD} data-testid="intake-contact-select"><SelectValue /></SelectTrigger>
        <SelectContent>{["email", "phone", "text"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    <Field label="How did you hear about us?"><Input className={FIELD} value={f.referral} onChange={set("referral")} data-testid="intake-referral-input" /></Field>
  </div>
);

const StepReview = ({ f }) => (
  <div className="space-y-6" data-testid="intake-review">
    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
      {[["Name", f.full_name], ["Email", f.email], ["Phone", f.phone], ["City", f.city || "—"], ["Filing Status", f.filing_status || "—"], ["Services", f.services.join(", ") || "—"], ["Business", f.business_owner ? f.business_name || "Yes" : "No"], ["Contact via", f.preferred_contact]].map(([k, v]) => (
        <div key={k} className="border-b border-white/10 pb-3"><dt className="text-xs uppercase tracking-[0.18em] text-white/45">{k}</dt><dd className="mt-1 text-white/90 capitalize-first">{v}</dd></div>
      ))}
    </dl>
    <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 flex gap-4" data-testid="intake-secure-docs-notice">
      <Lock className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
      <p className="text-sm text-white/75">Do not enter SSNs or upload tax documents here. After we review your intake, our team will guide you to a secure document submission method. <span className="text-[#D4AF37]">Encrypted document portal coming soon.</span></p>
    </div>
  </div>
);

const validate = (step, f) => {
  if (step === 0 && (!f.full_name.trim() || !f.email.includes("@") || !f.phone.trim())) return "Please complete your name, email and phone.";
  if (step === 1 && !f.services.length) return "Please select at least one service.";
  return null;
};

export default function GetStarted() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const next = () => { const err = validate(step, f); if (err) return toast.error(err); setStep(step + 1); };
  const submit = async () => {
    setBusy(true);
    try { await api.post("/intake", f); setDone(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const Body = [<StepAbout f={f} set={set} />, <StepServices f={f} setF={setF} />, <StepGoals f={f} set={set} />, <StepReview f={f} />][step];

  return (
    <div data-testid="get-started-page">
      <PageHero testid="intake-hero" eyebrow="Client Intake" title={<>Let's get <span className="italic gold-text">started.</span></>} sub="Four quick steps so we can understand your goals and prepare a strategy tailored to you." />
      <section className="bg-black py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {done ? (
            <div className="lux-card p-12 text-center" data-testid="intake-success">
              <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto" />
              <h2 className="mt-6 text-4xl font-medium">Intake received.</h2>
              <p className="mt-4 text-white/65">Thank you, {f.full_name.split(" ")[0]}. A member of the B2R team will reach out shortly.</p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center"><Link to="/book" className="btn-gold" data-testid="intake-success-book">Book a Consultation</Link><Link to="/" className="btn-glass">Back Home</Link></div>
            </div>
          ) : (
            <div className="lux-card p-8 md:p-12" data-testid="intake-form">
              <div className="flex justify-between text-xs tracking-[0.18em] uppercase mb-4">
                {STEPS.map((s, i) => <span key={s} className={i <= step ? "text-[#D4AF37]" : "text-white/35"} data-testid={`intake-step-label-${i}`}>{i + 1}. <span className="hidden sm:inline">{s}</span></span>)}
              </div>
              <Progress value={((step + 1) / STEPS.length) * 100} className="h-1 bg-white/10 [&>div]:bg-[#D4AF37] mb-10" />
              <h3 className="text-3xl font-medium mb-8">{STEPS[step]}</h3>
              {Body}
              <div className="mt-12 flex justify-between gap-4">
                <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} className="btn-glass disabled:opacity-30" data-testid="intake-back-button"><ArrowLeft className="w-4 h-4" /> Back</button>
                {step < 3
                  ? <button type="button" onClick={next} className="btn-gold" data-testid="intake-next-button">Continue <ArrowRight className="w-4 h-4" /></button>
                  : <button type="button" onClick={submit} disabled={busy} className="btn-gold" data-testid="intake-submit-button">{busy ? "Submitting…" : "Submit Intake"}</button>}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
